import { ProductSideImage } from '../types';

/**
 * Helper to convert a blob URL to base64 data URL
 */
export async function blobUrlToBase64(blobUrl: string): Promise<string> {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Helper to convert a File to base64 data URL
 */
export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Resolves the natural dimensions of an image File. Rejects on decode errors so
 * callers can distinguish a malformed image from a too-large one.
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to read image dimensions'));
        };
        img.src = url;
    });
}

/**
 * Helper to get a base64 data URL from a ProductSideImage
 */
export async function getSideImageAsBase64(sideImage: ProductSideImage | undefined): Promise<string | undefined> {
    if (!sideImage) return undefined;

    // If it has a file, convert to base64
    if (sideImage.file) {
        return fileToBase64(sideImage.file);
    }

    // If the URL is a blob URL, convert to base64
    if (sideImage.url.startsWith('blob:')) {
        return blobUrlToBase64(sideImage.url);
    }

    // If already a data URL or regular URL, return as-is
    return sideImage.url;
}
